import { defineStore } from 'pinia'
import TaskService from '@/services/TaskService'

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HOURS = Array.from({ length: 18 }, (_, index) => index + 5)

const DEFAULT_FIXED_BLOCKS = [
  { id: 'fixed-sleep', label: 'Sleep', dayOfWeek: -1, startHour: 23, endHour: 6, icon: 'mdi-sleep' },
  { id: 'fixed-gym', label: 'Gym', dayOfWeek: -1, startHour: 6, endHour: 7, icon: 'mdi-dumbbell' },
  { id: 'fixed-breakfast', label: 'Breakfast', dayOfWeek: -1, startHour: 7, endHour: 8, icon: 'mdi-coffee-outline' },
  { id: 'fixed-lunch', label: 'Lunch', dayOfWeek: -1, startHour: 13, endHour: 14, icon: 'mdi-food-apple-outline' },
  { id: 'fixed-dinner', label: 'Dinner', dayOfWeek: -1, startHour: 20, endHour: 21, icon: 'mdi-silverware-fork-knife' },
]

const DEFAULT_ENERGY_BLOCKS = [
  { id: 'energy-prep', startHour: 5, endHour: 8, level: 'prep' },
  { id: 'energy-high', startHour: 8, endHour: 12, level: 'high' },
  { id: 'energy-mid', startHour: 12, endHour: 14, level: 'mid' },
  { id: 'energy-low', startHour: 14, endHour: 17, level: 'low' },
]

const ENERGY_PROFILES = {
  lark: [
    { id: 'energy-prep', startHour: 5, endHour: 8, level: 'prep' },
    { id: 'energy-high', startHour: 8, endHour: 12, level: 'high' },
    { id: 'energy-mid', startHour: 12, endHour: 14, level: 'mid' },
    { id: 'energy-low', startHour: 14, endHour: 17, level: 'low' },
  ],
  owl: [
    { id: 'energy-prep', startHour: 8, endHour: 11, level: 'prep' },
    { id: 'energy-high', startHour: 11, endHour: 15, level: 'high' },
    { id: 'energy-low', startHour: 15, endHour: 17, level: 'low' },
    { id: 'energy-mid', startHour: 17, endHour: 20, level: 'mid' },
  ],
}

const clampHour = value => Math.max(0, Math.min(Number(value) || 0, 24))

const normalizeEnergyBlock = block => ({
  ...block,
  startHour: clampHour(block.startHour),
  endHour: clampHour(block.endHour),
})

const cloneEnergyBlocks = blocks => blocks.map(block => ({ ...block }))

const sortEnergyBlocks = blocks => [...blocks]
  .map(normalizeEnergyBlock)
  .sort((a, b) => a.startHour - b.startHour)

const ENERGY_META = {
  prep: {
    icon: 'mdi-weather-sunset-up',
    label: 'Preparation',
    shortLabel: 'Prep',
    title: 'Routine and Habit maker',
    description: 'Exercise, breakfast, meditation, shower, and intentional setup.',
    color: 'secondary',
  },
  high: {
    icon: 'mdi-fire',
    label: 'High Energy',
    shortLabel: 'High',
    title: 'Deep Focus',
    description: 'Strategy, intense coding, complex problem-solving, and high-stakes calls.',
    color: 'error',
  },
  mid: {
    icon: 'mdi-lightning-bolt',
    label: 'Mid Energy',
    shortLabel: 'Mid',
    title: 'Build / Execute',
    description: 'Routine coding, writing, organizing, emails, and standard meetings.',
    color: 'warning',
  },
  low: {
    icon: 'mdi-battery-low',
    label: 'Low Energy',
    shortLabel: 'Low',
    title: 'Admin / Recovery / Learn',
    description: 'Reading, tutorials, data entry, admin tasks, recovery, and rest.',
    color: 'info',
  },
}

const STARTER_TASKS = [
  { title: 'Plan next milestone', duration: 1, icon: 'mdi-flag-checkered' },
  { title: 'Review blockers', duration: 1, icon: 'mdi-alert-octagon-outline' },
  { title: 'Prepare weekly update', duration: 2, icon: 'mdi-file-chart-outline' },
]

const normalizeProjectId = project => project?._id || project?.id || project?.slug || project?.name
const normalizeProjectName = project => project?.name || project?.title || 'Untitled project'

const normalizeStatus = status => {
  const value = String(status || 'pending').toLowerCase().trim()
  if (['in progress', 'in-progress', 'active', 'started'].includes(value)) return 'in progress'
  if (['completed', 'complete', 'done', 'finished'].includes(value)) return 'completed'
  if (value === 'archived') return 'archived'
  return 'pending'
}

const normalizeOptionalNumber = value => value === null || value === undefined ? undefined : Number(value)

const extractItems = response => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data?.items)) return response.data.items
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.items)) return response.items
  return []
}

const extractItem = response => response?.data?.item || response?.data || response?.item || response

const normalizeTask = rawTask => {
  if (!rawTask) return null

  const bucketId = rawTask.project_id || rawTask.projectId || rawTask.bucketId

  return {
    id: rawTask.id || rawTask._id,
    title: rawTask.title || '',
    description: rawTask.description || '',
    status: normalizeStatus(rawTask.status),
    bucketId,
    duration: Number(rawTask.duration_hours ?? rawTask.duration ?? 1),
    scheduledDay: normalizeOptionalNumber(rawTask.scheduled_day ?? rawTask.scheduledDay),
    scheduledHour: normalizeOptionalNumber(rawTask.scheduled_hour ?? rawTask.scheduledHour),
    scheduledStart: rawTask.scheduled_start || rawTask.scheduledStart || null,
    scheduledEnd: rawTask.scheduled_end || rawTask.scheduledEnd || null,
    timezone: rawTask.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    icon: rawTask.icon || 'mdi-checkbox-marked-circle-outline',
    projectName: rawTask.project_name || rawTask.projectName,
    completedAt: rawTask.completed_at || rawTask.completedAt || null,
    createdAt: rawTask.created_at || rawTask.createdAt || new Date().toISOString(),
    updatedAt: rawTask.updated_at || rawTask.updatedAt || null,
    syncError: false,
  }
}

const weekDateForDayHour = (day, hour) => {
  if (day === undefined || day === null || hour === undefined || hour === null) return null

  const today = new Date()
  const todayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1
  const date = new Date(today)

  date.setDate(today.getDate() + (Number(day) - todayIndex))
  date.setHours(Number(hour), 0, 0, 0)

  return date
}

const toApiTaskPayload = task => {
  const scheduledStart = weekDateForDayHour(task.scheduledDay, task.scheduledHour)
  const scheduledEnd = scheduledStart
    ? new Date(scheduledStart.getTime() + (Number(task.duration || 1) * 60 * 60 * 1000))
    : null

  return {
    project_id: task.bucketId,
    title: task.title,
    description: task.description || undefined,
    status: normalizeStatus(task.status),
    duration_hours: Number(task.duration || 1),
    scheduled_day: task.scheduledDay ?? null,
    scheduled_hour: task.scheduledHour ?? null,
    scheduled_start: task.scheduledStart || scheduledStart?.toISOString() || null,
    scheduled_end: task.scheduledEnd || scheduledEnd?.toISOString() || null,
    timezone: task.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    icon: task.icon || undefined,
    completed_at: task.completedAt || null,
  }
}

const dateKeyForWeekday = weekday => {
  const today = new Date()
  const todayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1
  const date = new Date(today)

  date.setDate(today.getDate() + (weekday - todayIndex))
  date.setHours(0, 0, 0, 0)

  return date.toISOString().slice(0, 10)
}

const hoursOverlap = (startA, endA, startB, endB) => startA < endB && endA > startB

const fixedBlockApplies = (block, day, hour, duration = 1) => {
  if (block.dayOfWeek !== -1 && block.dayOfWeek !== day) return false

  const slotStart = hour
  const slotEnd = hour + duration

  if (block.startHour < block.endHour) {
    return hoursOverlap(slotStart, slotEnd, block.startHour, block.endHour)
  }

  // Overnight blocks, e.g. 23:00 → 06:00.
  return hoursOverlap(slotStart, slotEnd, block.startHour, 24) || hoursOverlap(slotStart, slotEnd, 0, block.endHour)
}

export const useWeekCanvasStore = defineStore('WeekCanvasStore', {
  state: () => ({
    days: DAYS,
    hours: HOURS,
    tasks: [],
    fixedBlocks: DEFAULT_FIXED_BLOCKS,
    energyBlocks: DEFAULT_ENERGY_BLOCKS,
    energyMeta: ENERGY_META,
    chronotype: 'lark',
    goldenDays: [],
    draggedTaskId: null,
    initialized: false,
    loading: false,
    saving: false,
    error: null,
    syncingTaskIds: [],
    lastSyncedAt: null,
  }),

  getters: {
    unscheduledTasks: state => bucketId => state.tasks.filter(task => (
      task.bucketId === bucketId &&
      !['completed', 'archived'].includes(task.status) &&
      task.scheduledDay === undefined
    )),

    scheduledTasks: state => state.tasks.filter(task => (
      task.status !== 'archived' &&
      task.scheduledDay !== undefined &&
      task.scheduledHour !== undefined
    )),

    completedTasks: state => state.tasks.filter(task => task.status === 'completed'),

    goldenDaySet: state => new Set(state.goldenDays),

    getTaskById: state => taskId => state.tasks.find(task => task.id === taskId),
  },

  actions: {
    setChronotype(chronotype) {
      if (!ENERGY_PROFILES[chronotype]) return
      this.chronotype = chronotype
      this.energyBlocks = cloneEnergyBlocks(ENERGY_PROFILES[chronotype])
    },

    updateEnergyBlock(blockId, updates) {
      this.energyBlocks = sortEnergyBlocks(this.energyBlocks.map(block => (
        block.id === blockId ? { ...block, ...updates } : block
      )))
    },

    updateEnergyBlocks(blocks = []) {
      this.energyBlocks = sortEnergyBlocks(blocks)
    },

    updateFixedBlock(blockId, updates) {
      this.fixedBlocks = this.fixedBlocks.map(block => (
        block.id === blockId ? { ...block, ...updates } : block
      ))
    },

    updateFixedBlocks(blocks = []) {
      this.fixedBlocks = blocks.map(block => ({ ...block }))
    },

    setTaskSyncing(taskId, syncing) {
      if (!taskId) return
      if (syncing && !this.syncingTaskIds.includes(taskId)) this.syncingTaskIds.push(taskId)
      if (!syncing) this.syncingTaskIds = this.syncingTaskIds.filter(id => id !== taskId)
    },

    replaceTask(localId, replacement) {
      const normalized = normalizeTask(replacement)
      if (!normalized?.id) return

      const index = this.tasks.findIndex(task => task.id === localId)
      if (index !== -1 && !normalized.projectName) normalized.projectName = this.tasks[index].projectName
      if (index === -1) this.tasks.push(normalized)
      else this.tasks.splice(index, 1, { ...this.tasks[index], ...normalized })
    },

    async hydrate(projects = [], params = {}) {
      const fetched = await this.fetchTasks(params)
      await this.fetchGoldenDays({ year: new Date().getFullYear() })

      if (!fetched.length && this.error) this.initializeFromProjects(projects)
      this.initialized = true
    },

    async fetchTasks(params = {}) {
      this.loading = true
      this.error = null

      try {
        const response = await TaskService.getTasks({ include_completed: true, ...params })
        const items = extractItems(response).map(normalizeTask).filter(Boolean)

        this.tasks = items
        this.lastSyncedAt = new Date().toISOString()

        return items
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Unable to load tasks'
        console.error('[WeekCanvasStore] fetchTasks:', error)
        return []
      } finally {
        this.loading = false
      }
    },

    async fetchGoldenDays(params = {}) {
      try {
        const response = await TaskService.getGoldenDays(params)
        const items = extractItems(response)

        this.goldenDays = items
          .map(item => item.date || item)
          .filter(Boolean)

        return this.goldenDays
      } catch (error) {
        console.error('[WeekCanvasStore] fetchGoldenDays:', error)
        return this.goldenDays
      }
    },

    initializeFromProjects(projects = []) {
      const sourceProjects = projects.length
        ? projects
        : [{ id: 'project-general', name: 'General Operations', color: 'primary', icon: 'mdi-view-dashboard-outline' }]

      sourceProjects.forEach(project => {
        const projectId = normalizeProjectId(project)
        if (!projectId) return

        const hasProjectTasks = this.tasks.some(task => task.bucketId === projectId)
        if (hasProjectTasks) return

        STARTER_TASKS.forEach((starterTask, index) => {
          this.tasks.push({
            id: `week-task-${projectId}-${index + 1}`,
            title: starterTask.title,
            status: 'pending',
            bucketId: projectId,
            duration: starterTask.duration,
            icon: starterTask.icon,
            projectName: normalizeProjectName(project),
            createdAt: new Date().toISOString(),
          })
        })
      })

      this.initialized = true
    },

    async addTask({ bucketId, projectName, title, duration = 1, icon = 'mdi-checkbox-marked-circle-plus-outline' }) {
      if (!bucketId || !title?.trim()) return null

      const task = {
        id: `week-task-${bucketId}-${Date.now()}`,
        title: title.trim(),
        status: 'pending',
        bucketId,
        duration: Math.max(1, Math.min(Number(duration) || 1, 2)),
        icon,
        projectName: projectName || 'Untitled project',
        createdAt: new Date().toISOString(),
        syncError: false,
      }

      this.tasks.push(task)

      this.saving = true
      this.setTaskSyncing(task.id, true)

      try {
        const response = await TaskService.createTask(toApiTaskPayload(task))
        this.replaceTask(task.id, extractItem(response))
        this.lastSyncedAt = new Date().toISOString()
      } catch (error) {
        task.syncError = true
        this.error = error.response?.data?.message || error.message || 'Unable to create task'
        console.error('[WeekCanvasStore] addTask:', error)
      } finally {
        this.setTaskSyncing(task.id, false)
        this.saving = false
      }

      return task
    },

    getEnergyLevel(hour) {
      const block = this.energyBlocks.find(item => hour >= item.startHour && hour < item.endHour)
      return block?.level || 'mid'
    },

    getFixedBlock(day, hour, duration = 1) {
      return this.fixedBlocks.find(block => fixedBlockApplies(block, day, hour, duration))
    },

    hasFixedBlock(day, hour, duration = 1) {
      return !!this.getFixedBlock(day, hour, duration)
    },

    getTaskAt(day, hour) {
      return this.scheduledTasks.find(task => {
        const start = task.scheduledHour
        const end = task.scheduledHour + (task.duration || 1)

        return task.scheduledDay === day && hour >= start && hour < end
      })
    },

    hasCollision(day, hour, duration = 1, ignoreTaskId = null) {
      if (hour < this.hours[0] || hour + duration > this.hours[this.hours.length - 1] + 1) return true
      if (this.hasFixedBlock(day, hour, duration)) return true

      return this.scheduledTasks.some(task => {
        if (task.id === ignoreTaskId) return false
        if (task.scheduledDay !== day) return false

        const taskStart = task.scheduledHour
        const taskEnd = task.scheduledHour + (task.duration || 1)

        return hoursOverlap(hour, hour + duration, taskStart, taskEnd)
      })
    },

    async scheduleTask(taskId, day, hour, duration = 1) {
      const task = this.getTaskById(taskId)
      if (!task) return false

      const normalizedDuration = Math.max(1, Math.min(Number(duration) || 1, 4))
      if (this.hasCollision(day, hour, normalizedDuration, taskId)) return false

      const snapshot = { ...task }

      task.scheduledDay = day
      task.scheduledHour = hour
      task.duration = normalizedDuration
      task.status = task.status === 'archived' ? 'pending' : normalizeStatus(task.status)
      task.completedAt = undefined

      this.setTaskSyncing(task.id, true)

      try {
        const response = await TaskService.scheduleTask(task.id, toApiTaskPayload(task))
        this.replaceTask(task.id, extractItem(response))
        this.lastSyncedAt = new Date().toISOString()
      } catch (error) {
        Object.assign(task, snapshot, { syncError: true })
        this.error = error.response?.data?.message || error.message || 'Unable to schedule task'
        console.error('[WeekCanvasStore] scheduleTask:', error)
        return false
      } finally {
        this.setTaskSyncing(task.id, false)
      }

      return true
    },

    async updateTaskStatus(taskId, status, options = {}) {
      const task = this.getTaskById(taskId)
      if (!task) return false

      const snapshot = { ...task }
      const nextStatus = normalizeStatus(status)

      task.status = nextStatus
      task.completedAt = nextStatus === 'completed'
        ? (options.completedAt || task.completedAt || new Date().toISOString())
        : undefined

      this.setTaskSyncing(task.id, true)

      try {
        const response = await TaskService.updateTask(task.id, toApiTaskPayload(task))
        this.replaceTask(task.id, extractItem(response))
        this.lastSyncedAt = new Date().toISOString()
        return true
      } catch (error) {
        Object.assign(task, snapshot, { syncError: true })
        this.error = error.response?.data?.message || error.message || 'Unable to update task status'
        console.error('[WeekCanvasStore] updateTaskStatus:', error)
        return false
      } finally {
        this.setTaskSyncing(task.id, false)
      }
    },

    async startTask(taskId) {
      return this.updateTaskStatus(taskId, 'in progress')
    },

    async finishTask(taskId) {
      return this.updateTaskStatus(taskId, 'completed', { completedAt: new Date().toISOString() })
    },

    async completeTask(taskId) {
      return this.finishTask(taskId)
    },

    async returnTask(taskId) {
      const task = this.getTaskById(taskId)
      if (!task) return

      const snapshot = { ...task }

      task.scheduledDay = undefined
      task.scheduledHour = undefined
      task.status = 'pending'
      task.completedAt = undefined

      this.setTaskSyncing(task.id, true)

      try {
        const response = await TaskService.returnTask(task.id)
        this.replaceTask(task.id, extractItem(response))
        this.lastSyncedAt = new Date().toISOString()
      } catch (error) {
        Object.assign(task, snapshot, { syncError: true })
        this.error = error.response?.data?.message || error.message || 'Unable to return task'
        console.error('[WeekCanvasStore] returnTask:', error)
      } finally {
        this.setTaskSyncing(task.id, false)
      }
    },

    async sweepCompletedTask(taskId) {
      const task = this.getTaskById(taskId)
      if (!task || task.status !== 'completed') return

      const key = task.scheduledDay === undefined ? new Date().toISOString().slice(0, 10) : dateKeyForWeekday(task.scheduledDay)
      const snapshot = { ...task }
      if (!this.goldenDays.includes(key)) this.goldenDays.push(key)
      task.status = 'archived'

      this.setTaskSyncing(task.id, true)
      try {
        await TaskService.markGoldenDay({ date: key, source_task_id: task.id })
        const response = await TaskService.updateTask(task.id, toApiTaskPayload(task))
        this.replaceTask(task.id, extractItem(response))
        this.lastSyncedAt = new Date().toISOString()
      } catch (error) {
        Object.assign(task, snapshot, { syncError: true })
        this.error = error.response?.data?.message || error.message || 'Unable to mark golden day'
        console.error('[WeekCanvasStore] sweepCompletedTask:', error)
      } finally {
        this.setTaskSyncing(task.id, false)
      }
    },

    async cleanCompletedTasks() {
      const completedTaskIds = this.completedTasks.map(task => task.id)

      await Promise.all(completedTaskIds.map(taskId => this.sweepCompletedTask(taskId)))
    },

    async setTaskDuration(taskId, duration) {
      const task = this.getTaskById(taskId)
      if (!task) return

      const normalizedDuration = Math.max(1, Math.min(Number(duration) || 1, 4))
      if (
        task.scheduledDay !== undefined &&
        task.scheduledHour !== undefined &&
        this.hasCollision(task.scheduledDay, task.scheduledHour, normalizedDuration, task.id)
      ) {
        return
      }

      const snapshot = { ...task }
      task.duration = normalizedDuration

      this.setTaskSyncing(task.id, true)

      try {
        const response = await TaskService.updateTask(task.id, toApiTaskPayload(task))
        this.replaceTask(task.id, extractItem(response))
        this.lastSyncedAt = new Date().toISOString()
      } catch (error) {
        Object.assign(task, snapshot, { syncError: true })
        this.error = error.response?.data?.message || error.message || 'Unable to update task duration'
        console.error('[WeekCanvasStore] setTaskDuration:', error)
      } finally {
        this.setTaskSyncing(task.id, false)
      }
    },

    setDraggedTask(taskId) {
      this.draggedTaskId = taskId
    },

    clearDraggedTask() {
      this.draggedTaskId = null
    },
  },
})
